import React, { useState } from "react";
import { EditIcon, EyeIcon, Loader2, PaletteIcon, RocketIcon, SwatchBookIcon } from "lucide-react";
import { Button } from "@stackje/shared-components/ui/button";
import { BottomBar } from "@/components/shared/BottomBar";
import { ScaledPreviewContainer } from "@stackje/shared-components/ui/scaled-preview-container";

interface TabsWithFabProps {
  formContent: React.ReactNode;
  previewContent: React.ReactNode;
  switchSubTypeSelection?: () => void;
  switchThemeSelection?: () => void;
  onFormInputChange?: () => void;
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
  isSubmitting: boolean;
  formId: string;
  submitLabel?: string;
}

export function TabsWithFab({
  formContent,
  previewContent,
  switchSubTypeSelection,
  switchThemeSelection,
  onFormInputChange,
  onSubmit,
  isSubmitting,
  formId,
  submitLabel = "Save",
}: TabsWithFabProps) {
  const [activeTab, setActiveTab] = useState(0); // 0 = edit, 1 = preview

  return (
    <>
      {/* Floating Action Button for Edit/Preview Toggle */}
      <button
        onClick={() => setActiveTab(activeTab === 0 ? 1 : 0)}
        className="fixed bottom-24 right-8 z-20 bg-primary text-primary-foreground rounded-full p-4 shadow-lg hover:bg-primary/90 hover:scale-[0.9] transition-all duration-200"
        aria-label={activeTab === 0 ? "Switch to Preview" : "Switch to Edit"}
      >
        {activeTab === 0 ? (
          <EyeIcon className="size-6" />
        ) : (
          <EditIcon className="size-6" />
        )}
      </button>
      
      <form
        method="post"
        id={formId}
        onChange={onFormInputChange}
        onSubmit={onSubmit}
      >
        {/* Content with independent heights - only render active tab */}
        <div>
          {activeTab === 0 ? (
            /* Edit Tab */
            <div className="space-y-4 pt-8 px-8 pb-16 max-w-2xl mx-auto">
              {formContent}
            </div>
          ) : (
            /* Preview Tab */
            <div className="w-full flex items-center justify-center pt-8 pb-8 min-h-full">
              <ScaledPreviewContainer>
                {previewContent}
              </ScaledPreviewContainer>
            </div>
          )}
          
          {/* Always render form content invisibly to ensure hidden inputs are available for submission */}
          {activeTab === 1 && (
            <div style={{ display: 'none' }}>
              {formContent}
            </div>
          )}
        </div>
        
        {/* Consistent Bottom Bar */}
        <BottomBar>
          <div className="flex items-center justify-between w-full">
            <Button
              type="button"
              disabled={!switchSubTypeSelection}
              onClick={() => {
                switchSubTypeSelection?.();
                setActiveTab(1); // Switch to preview tab
              }}
              variant="ghost"
            >
              <SwatchBookIcon className="h-5 w-5" />
            </Button>
            <Button
              type="submit"
              className="text-white px-12"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <RocketIcon className="h-5 w-5 mr-2" />
                  {submitLabel}
                </>
              )}
            </Button>
            <Button 
              type="button" 
              variant="ghost" 
              onClick={() => {
                switchThemeSelection?.();
                setActiveTab(1); // Switch to preview tab
              }}
            >
              <PaletteIcon className="h-5 w-5" />
            </Button>
          </div>
        </BottomBar>
      </form>
    </>
  );
}